import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Logo } from '@/components/generals/logo'
import { useContact } from '@/components/generals/contact-dialog'
import { footerColumns } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

import {
    FacebookIcon,
    InstagramIcon,
    LinkedInIcon,
    TikTokIcon,
    XIcon,
    YouTubeIcon
} from '@/components/generals/social-icons'

const socialIcons = {
    x: XIcon,
    youtube: YouTubeIcon,
    instagram: InstagramIcon,
    linkedin: LinkedInIcon,
    tiktok: TikTokIcon,
    facebook: FacebookIcon
}

const linkClasses = 'rounded text-[14px] leading-6 text-slate transition-colors duration-200 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'

export const Footer: FC = (): ReactNode => {
    const { openContact } = useContact()

    return (
        <footer className='border-t border-hairline bg-white'>
            <div className='fc-container pb-10 pt-16 md:pt-20'>
                <div className='flex flex-col gap-12 lg:flex-row lg:justify-between lg:gap-16'>
                    <div className='flex flex-col items-start gap-5 lg:w-[260px] lg:shrink-0'>
                        <Link href='/' aria-label='FlowCraft home' className='rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-violet'>
                            <Logo />
                        </Link>

                        <p className='text-[15px] font-medium leading-snug tracking-[-0.01em] text-graphite'>
                            Wire up every tool.
                            <br />
                            Run every workflow.
                        </p>
                    </div>

                    <div className='grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 lg:w-[720px] lg:gap-x-14'>
                        {footerColumns.map(column => (
                            <nav key={column.title} aria-label={column.title} className='flex flex-col gap-4'>
                                <h2 className='text-[13px] font-medium text-silver'>
                                    {column.title}
                                </h2>

                                <ul className='flex flex-col gap-2'>
                                    {column.links.map(link => (
                                        <li key={link.label}>
                                            {link.href ? (
                                                <Link href={link.href} className={linkClasses}>
                                                    {link.label}
                                                </Link>
                                            ) : (
                                                <button type='button' onClick={() => openContact({ intent: link.intent })} className={`${linkClasses} text-left`}>
                                                    {link.label}
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        ))}
                    </div>
                </div>

                <div className='mt-16 flex flex-col gap-6 border-t border-hairline pt-8 sm:flex-row sm:items-center sm:justify-between'>
                    <p className='text-[13px] text-silver'>
                        &copy; 2026 FlowCraft, Inc. All rights reserved.
                    </p>

                    <nav aria-label='Social media' className='flex items-center gap-5'>
                        {siteConfig.socials.map(social => {
                            const Icon = socialIcons[social.id]

                            return (
                                <a
                                    key={social.id}
                                    href={social.href}
                                    target='_blank'
                                    rel='noreferrer'
                                    aria-label={social.label}
                                    className='rounded text-silver transition-colors duration-200 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-violet'
                                >
                                    <Icon className='size-4' />
                                </a>
                            )
                        })}
                    </nav>
                </div>
            </div>
        </footer>
    )
}
